import React from "react";

import { Check, Loader2, Mic, X } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@needl-ai/common/components/ui/tooltip";

type SpeechState = "idle" | "recording" | "processing";

interface SpeechButtonProps {
  speechState: SpeechState;
  onStart: () => void;
  onStop: () => void;
  onCancel: () => void;
  disabled?: boolean;
  isError?: boolean;
}

const SpeechButton: React.FC<SpeechButtonProps> = ({
  speechState,
  onStart,
  onStop,
  onCancel,
  disabled = false,
  isError = false,
}) => {
  const isRecording = speechState === "recording";
  const isProcessing = speechState === "processing";

  if (isProcessing) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            disabled
            className="p-3 rounded-full bg-gray-200 text-gray-400 cursor-wait flex items-center justify-center"
            data-testid="speech-button"
          >
            <Loader2 size={16} className="animate-spin" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Transcribing...</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (isRecording) {
    return (
      <div className="flex items-center gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onCancel}
              className="p-2.5 rounded-full transition-all duration-200 flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700"
              data-testid="speech-cancel-button"
            >
              <X size={18} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Cancel</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onStop}
              className="p-2.5 rounded-full transition-all duration-200 flex items-center justify-center bg-primary text-white shadow-md hover:bg-primary/80"
              data-testid="speech-submit-button"
            >
              <Check size={18} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Submit recording</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onStart}
          disabled={disabled}
          className={`p-3 rounded-full transition-all duration-200 flex items-center justify-center ${
            disabled
              ? "opacity-50 cursor-not-allowed bg-gray-100 text-gray-400"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
          }`}
          data-testid="speech-button"
        >
          <Mic size={16} />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{isError ? "Transcription failed. Try again." : "Voice input"}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export { SpeechButton };
export type { SpeechState };
